import React, { forwardRef, useMemo, useImperativeHandle } from 'react'
import styled from 'styled-components'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import { useClipboardProcessorsWithGroupRows } from '../../../../shared/components/custom/Grid/useClipboardProcessor'
import { groupCellWithActionsStyles } from '../../../../shared/components/custom/Grid/cellRenderers/CellComponents'
import { AccountMapRule } from '../../types/AccountMapRule'
import { CompanyColumnInfo } from '../../utils/accountMapUtils'
import { StatementRow } from '../../../../types/statementRow/StatementRow'
import { useAccountMapColumns } from './useAccountMapColumns'
import { useAccountMapGridData } from './useAccountMapGridData'
import { useAccountMapGridCallbacks } from './useAccountMapGridCallbacks'
import { AccountMapGridContext } from './StatementRowGroupCell'

const GridContainer = styled.div`
  height: calc(100vh - 200px);
  min-height: 400px;
  ${groupCellWithActionsStyles}

  /* Hide expand icon for parent rows */
  .ag-row-parent-group {
    .ag-group-contracted,
    .ag-group-expanded {
      display: none !important;
    }
  }

  /* Alternating colors for company column groups - header styling */
  .ag-header-cell.company-odd,
  .ag-header-group-cell.company-odd {
    background-color: rgba(0, 0, 0, 0.06);
  }

  /* Validation: incomplete from/to pair */
  .ag-cell.cell-validation-error {
    background-color: rgba(255, 77, 79, 0.12) !important;
    border-color: #ff4d4f !important;
  }
`

export interface AccountMapRuleGridRef {
  getRowData: () => import('../../utils/accountMapUtils').AccountMapGridRow[]
  getRules: () => AccountMapRule[]
  hasChanges: () => boolean
  validate: () => boolean
  discardChanges: () => void
  resetToServerData: () => void
}

interface AccountMapRuleGridProps {
  /** Companies to show as column groups */
  companies: CompanyColumnInfo[]
  /** Ordered list of statementRows - determines display order */
  statementRows?: StatementRow[]
  /** Optional callback when data changes */
  onChange?: (rules: AccountMapRule[]) => void
  /** Enable editing (default: true) */
  editable?: boolean
}

const AccountMapRuleGrid = forwardRef<AccountMapRuleGridRef, AccountMapRuleGridProps>(
  ({ companies, statementRows, onChange, editable = true }, ref) => {
    const companyIds = useMemo(() => companies.map(c => c.companyId), [companies])

    const {
      gridRef,
      rowData,
      resetKey,
      validationErrors,
      isLoading,
      handleAddRowForStatementRow,
      onCellValueChanged,
      onUndoStarted,
      onUndoEnded,
      onRedoStarted,
      onRedoEnded,
      getRefMethods
    } = useAccountMapGridData({ companyIds, statementRows, editable, onChange })

    const {
      getStatementRowName,
      statementRowsById,
      autoGroupColumnDef,
      getRowId,
      getRowStyle,
      getRowClass,
      isGroupOpenByDefault
    } = useAccountMapGridCallbacks({ statementRows })

    const { colDefs, defaultColDef, cellSelection } = useAccountMapColumns({
      companies,
      editable,
      getStatementRowName
    })

    const { processCellForClipboard, processDataFromClipboard } = useClipboardProcessorsWithGroupRows()

    const context = useMemo<AccountMapGridContext>(
      () => ({
        validationErrors,
        statementRowsById,
        getStatementRowName,
        editable,
        onAddRow: handleAddRowForStatementRow
      }),
      [validationErrors, statementRowsById, getStatementRowName, editable, handleAddRowForStatementRow]
    )

    useImperativeHandle(ref, getRefMethods, [getRefMethods])

    return (
      <GridContainer>
        <CustomAgGrid
          key={resetKey}
          ref={gridRef}
          context={context}
          rowData={rowData}
          columnDefs={colDefs}
          cellSelection={cellSelection}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          getRowClass={getRowClass}
          isGroupOpenByDefault={isGroupOpenByDefault}
          onCellValueChanged={onCellValueChanged}
          processCellForClipboard={processCellForClipboard}
          processDataFromClipboard={processDataFromClipboard}
          onUndoStarted={onUndoStarted}
          onUndoEnded={onUndoEnded}
          onRedoStarted={onRedoStarted}
          onRedoEnded={onRedoEnded}
          rowSelection="multiple"
          suppressGroupRowsSticky
          undoRedoCellEditing
          undoRedoCellEditingLimit={20}
          enterNavigatesVertically
          enterNavigatesVerticallyAfterEdit
          stopEditingWhenCellsLoseFocus
          loading={isLoading}
        />
      </GridContainer>
    )
  }
)

AccountMapRuleGrid.displayName = 'AccountMapRuleGrid'

export default AccountMapRuleGrid
